// eslint-disable-next-line
import { makeAutoObservable, runInAction } from "mobx";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";
import { IChangeable } from "../../../stores/types";
import { TYPE, rootContainer } from '../../../services/container/root';
import ShipmentsService, { IConnection } from "../../../services/api/shipments-service";
import { IOption } from "../../../components/inputs/select-dropdown-multi/select-dropdown-multi";
import { SHIPMENT_STATUS_TRANSLATION_KEYS_MAP } from "../shipment.types";
import { SHIPMENT_STATUS_ENUM } from "../../manage-returns/rma-page/rma-types";
import { IReportFiltersType } from "../../../services/api/report-service";

interface IShipmentListFilters {
    search_input: string,
    filter_status: SHIPMENT_STATUS_ENUM[] | null,
    filter_carrier_id: string[] | null,
    filter_connection_id: string[] | null,
    filter_create_date: string,
    filter_order_or_rma_create_date: string,
    filter_delivery_date: string,
    filter_last_update_date: string,
}
interface IShipmentListFiltersChanges extends Partial<IShipmentListFilters> { };

export interface IFiltersButtonstyle {
    variant: BUTTON_VARIANTS,
    color: string,
}

class ShipmentListFiltersStore implements IChangeable<IShipmentListFilters> {
    private _isLoaded: boolean = false;
    private _isLoading: boolean = false;
    private _model: IShipmentListFilters;
    private _changes: IShipmentListFiltersChanges = {};
    private _carriers: string[] = [];
    private _connections: IConnection[] = [];
    private _isFiltersApplied: boolean = false;
    // TODO: When adding translations for this page, change this scope. Currently it translates all the statuses for the filter drawer
    private _scope = 'dashboard-returns-rma-card';

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = this._createModelFromQuery();
    }

    private get shipmentsService(): ShipmentsService {
        return rootContainer.get<ShipmentsService>(TYPE.ShipmentsService);
    }

    public get searchInput() {
        return this._changes.search_input ?? this._model.search_input;
    }

    get isFiltersApplied() {
        return this._isFiltersApplied;
    }

    get isLoading() {
        return this._isLoading;
    }

    get selectedStatus() {
        if (this._changes.filter_status === null) {
            return null;
        }
        return this._changes.filter_status ?? this._model.filter_status;
    }

    get selectedCarrierId() {
        if (this._changes.filter_carrier_id === null) {
            return null;
        }
        return this._changes.filter_carrier_id ?? this._model.filter_carrier_id;
    }

    get selectedConnectionId() {
        if (this._changes.filter_connection_id === null) {
            return null;
        }
        return this._changes.filter_connection_id ?? this._model.filter_connection_id;
    }

    get createDate() {
        return this._changes.filter_create_date ?? this._model.filter_create_date;
    }

    get orderOrRmaCreateDate() {
        return this._changes.filter_order_or_rma_create_date ?? this._model.filter_order_or_rma_create_date;
    }

    get deliveryDate() {
        return this._changes.filter_delivery_date ?? this._model.filter_delivery_date;
    }

    get lastUpdateDate() {
        return this._changes.filter_last_update_date ?? this._model.filter_last_update_date;
    }

    private get _allOption() {
        return {
            label: 'All',
            value: 0 /** Select dropdown options doesn't accept null */
        }
    }

    public getShipmentStatusOptions(getTranslation: (key: string, scope: string) => string, scope: string): IOption[] {
        return [
            {
                value: -1, /** Can't use 0, because SHIPMENT_STATUS_ENUM.PENDING_SHIPMENT is also 0 */
                label: 'All',
            },
            ...Object.values(SHIPMENT_STATUS_ENUM)
                // Filter out verified and pending shipment statuses cause verified has the same label as received and pending shipment is not a valid status for this page.
                .filter(status => typeof status === 'number' && status !== SHIPMENT_STATUS_ENUM.VERIFIED && status !== SHIPMENT_STATUS_ENUM.PENDING_SHIPMENT)
                .map(status => ({
                    value: status,
                    label: SHIPMENT_STATUS_TRANSLATION_KEYS_MAP[status as keyof typeof SHIPMENT_STATUS_TRANSLATION_KEYS_MAP] ?? String(status),
                }))
        ];
    }

    get carrierIdOptions() { 
        return [
            this._allOption,
            ...this._carriers.map(carrier => ({
                value: carrier,
                label: carrier
            }))
        ];
    }

    get connectionOptions() {
        return [
            this._allOption,
            ...this._connections.map(connection => ({
                value: connection.id,
                label: connection.name
            }))
        ];
    }

    public getSelectedShipmentStatusValue(getTranslation: (key: string, scope: string) => string, scope: string) {
        if (!this.selectedStatus) {
            return [this.getShipmentStatusOptions(getTranslation, scope)[0]];
        }
        
        const selectedOptions = this.getShipmentStatusOptions(getTranslation, scope).filter(option => 
            option.value !== -1 &&
            this.selectedStatus?.includes(option.value as SHIPMENT_STATUS_ENUM)
        );
        
        if (selectedOptions.length === 0) {
            return [this.getShipmentStatusOptions(getTranslation, scope)[0]];
        }
        
        return selectedOptions;
    }

    get selectedConnectionValue() {
        if (!this.selectedConnectionId) {
            return [this._allOption];
        }
        
        const selectedOptions = this.connectionOptions.filter(option => 
            option.value !== 0 &&
            this.selectedConnectionId?.includes(option.value as string)
        );
        
        if (selectedOptions.length === 0) {
            return [this._allOption];
        }
        
        return selectedOptions;
    }

    get selectedCarrierIdValue() {
        if (!this.selectedCarrierId) {
            return [this._allOption];
        }
        
        const selectedOptions = this.carrierIdOptions.filter(option => 
            option.value !== 0 &&
            this.selectedCarrierId?.includes(option.value as string)
        );
        
        if (selectedOptions.length === 0) {
            return [this._allOption];
        }
        
        return selectedOptions;
    }

    public setSearchValue(value: string) {
        this._changes.search_input = value;
        this._clearUnneededChanges();
    }

    public setSelectedShipmentStatus(value: SHIPMENT_STATUS_ENUM[] | null) {
        this._changes.filter_status = value;
        this._clearUnneededChanges();
    }

    public setSelectedCarrierId(value: string[] | null) {
        this._changes.filter_carrier_id = value;
        this._clearUnneededChanges();
    }

    public setSelectedConnectionId(value: string[] | null) {
        this._changes.filter_connection_id = value;
        this._clearUnneededChanges();
    }

    public setSelectedCreateDate(value: string) {
        this._changes.filter_create_date = value;
        this._clearUnneededChanges();
    }

    public setSelectedOrderOrRmaCreateDate(value: string) {
        this._changes.filter_order_or_rma_create_date = value;
        this._clearUnneededChanges();
    }

    public setSelectedDeliveryDate(value: string) {
        this._changes.filter_delivery_date = value;
        this._clearUnneededChanges();
    }

    public setSelectedLastUpdateDate(value: string) {
        this._changes.filter_last_update_date = value;
        this._clearUnneededChanges();
    }
    
    public async loadFilterOptions() {
        if (this._isLoaded) {
            return;
        }
        try {
            this._isLoading = true;
            const response = await this.shipmentsService.getFilterOptions();
            runInAction(() => {
                this._carriers = response.carriers;
                this._connections = response.connections;
                this._isLoading = false;
                this._isLoaded = true;
            });
        } catch (error) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    private _createModelFromQuery(): IShipmentListFilters {
        let params = new URLSearchParams(document.location.search);

        const searchInput = params.get('search_input');

        return {
            search_input: searchInput ?? '',
            filter_status: null,
            filter_carrier_id: [],
            filter_connection_id: [],
            filter_create_date: '',
            filter_order_or_rma_create_date: '',
            filter_delivery_date: '',
            filter_last_update_date: '',
        }
    }

    public getReportFilters(): IReportFiltersType {
        return {
            filter_status: this.selectedStatus?.join(',') || '',
            search_query: this.searchInput || '',
            filter_carrier_id: this.selectedCarrierId?.join(',') || '',
            filter_connection_id: this.selectedConnectionId?.join(',') || '',
            filter_create_date: this.createDate || '',
            filter_order_or_rma_create_date: this.orderOrRmaCreateDate || '',
            filter_delivery_date: this.deliveryDate || '',
            filter_last_update_date: this.lastUpdateDate || '',
            
            /** Order Tracking report uses filter_date (ISO UTC format) for date filtering */
            filter_date: this.createDate || 'all',
        }
    }

    public getUpdatedModel(): IShipmentListFilters {
        return { ...this._model, ...this._changes };;
    }

    public commitChanges(): void {
        this._model = this.getUpdatedModel();
        this._isFiltersApplied = this.hasActiveDrawerFilters();
        this.discardChanges();
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    public hasActiveDrawerFilters(): boolean {
        const filters = this.getUpdatedModel();
        const { search_input, ...drawerFilters } = filters;
        
        return Object.entries(drawerFilters).some(([key, value]) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (typeof value === 'string') {
                return value !== '';
            }
            return !!value;
        });
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IShipmentListFilters;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

}

export default ShipmentListFiltersStore;
